import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Avatar, hueFor } from "@/components/ChatMessageBubbles";
import {
  chatAccountLabel,
  chatActionError,
  openChatDm,
  searchChatUsers,
  type ChatAccount,
  type ChatChannel,
  type ChatUser,
} from "@/lib/chatConnect";

/** Slack group DMs hold at most 8 people besides you. */
const MAX_PEOPLE = 8;

type Props = {
  /** Slack logins that can start DMs; a workspace picker shows when there are several. */
  accounts: ChatAccount[];
  initialAccountId: string;
  onClose: () => void;
  onOpened: (accountId: string, channel: ChatChannel) => void;
};

export function ChatNewDmPanel({ accounts, initialAccountId, onClose, onOpened }: Props) {
  const [accountId, setAccountId] = useState(initialAccountId);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ChatUser[]>([]);
  const [picked, setPicked] = useState<ChatUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const account = accounts.find((a) => a.id === accountId) || accounts[0];

  useEffect(() => {
    inputRef.current?.focus();
  }, [accountId]);

  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      searchChatUsers(account.id, query)
        .then((users) => {
          if (cancelled) return;
          setResults(users);
          setError("");
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(chatActionError(err, "Could not load people in this workspace."));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [account, query]);

  function switchAccount(next: string) {
    setAccountId(next);
    setPicked([]);
    setResults([]);
    setQuery("");
    setError("");
    setNeedsReconnect(false);
  }

  function toggle(user: ChatUser) {
    setPicked((prev) => {
      if (prev.some((u) => u.id === user.id)) return prev.filter((u) => u.id !== user.id);
      return prev.length >= MAX_PEOPLE ? prev : [...prev, user];
    });
  }

  async function start() {
    if (!account || !picked.length || opening) return;
    setOpening(true);
    setError("");
    setNeedsReconnect(false);
    try {
      onOpened(account.id, await openChatDm(account.id, picked.map((u) => u.id)));
    } catch (err) {
      setNeedsReconnect(err instanceof Error && err.message === "missing_scope");
      setError(chatActionError(err, "Could not start the conversation."));
      setOpening(false);
    }
  }

  if (!account) return null;

  return (
    <section className="chat-new-dm" aria-label="New chat">
      <div className="chat-new-dm-head">
        <strong>New chat</strong>
        <button type="button" className="icon-btn" aria-label="Close new chat" onClick={onClose}>
          <X size={16} />
        </button>
      </div>
      {accounts.length > 1 ? (
        <label className="chat-new-dm-workspace">
          <span className="muted">Workspace</span>
          <select value={account.id} onChange={(event) => switchAccount(event.target.value)}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {chatAccountLabel(a)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {picked.length ? (
        <div className="chat-new-dm-picked">
          {picked.map((user) => (
            <button
              key={user.id}
              type="button"
              className="chat-new-dm-chip"
              aria-label={`Remove ${user.name}`}
              onClick={() => toggle(user)}
            >
              {user.name}
              <X size={12} aria-hidden />
            </button>
          ))}
        </div>
      ) : null}
      <input
        ref={inputRef}
        className="chat-picker-search"
        type="search"
        placeholder={`Search people in ${chatAccountLabel(account)}`}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
      />
      {error ? (
        <p className="banner-error" role="alert">
          {error}
          {needsReconnect ? (
            <>
              {" "}
              <Link to="/settings/connections/chat">Open Connections</Link>
            </>
          ) : null}
        </p>
      ) : null}
      <ul className="chat-new-dm-list">
        {results.map((user) => {
          const on = picked.some((u) => u.id === user.id);
          return (
            <li key={user.id}>
              <button
                type="button"
                className={`chat-new-dm-user${on ? " active" : ""}`}
                aria-pressed={on}
                onClick={() => toggle(user)}
              >
                <Avatar name={user.name} url={user.avatarUrl} hue={hueFor(user.id)} />
                <span>{user.name}</span>
              </button>
            </li>
          );
        })}
        {loading && !results.length ? <li className="muted pad">Loading people…</li> : null}
        {!loading && !results.length && !error ? <li className="muted pad">No one matches.</li> : null}
      </ul>
      <div className="chat-new-dm-actions">
        <span className="muted">
          {picked.length > 1 ? `Group message · ${picked.length} people` : picked.length ? "Direct message" : ""}
        </span>
        <button
          type="button"
          className="primary-btn"
          disabled={!picked.length || opening}
          onClick={() => void start()}
        >
          {opening ? "Opening…" : "Start"}
        </button>
      </div>
    </section>
  );
}
